// ============================================================
// SERVER TYPES — pure data contracts between client and server
// ============================================================
//
// This module is the source of truth for data that crosses the
// eventual network boundary (Phase 3). Nothing here should depend
// on rendering, DOM, or animation.
//
// Naming conventions follow SRS v2 §2.7 "Network Payloads & RPC".

import type { BattleUnit, Base, Pos } from '../BattleUnit.ts';
import type { SpellDef } from '../constants.ts';
import type { TerrainCell, TerrainGrid } from './terrain.ts';

// =============================================================
// STATE
// =============================================================

/** A snapshot of the entire authoritative battle state at a point in time. */
export interface ServerState {
  units: BattleUnit[];
  bases: Base[];
  /** Which macro-phase the battle is currently in. */
  phase: ServerPhase;
  /** Current round number (incremented after each EXEC completes). */
  round: number;
  /**
   * 三消遺留欄位（現已不讀，僅 init/clone/reset 保留以免動到引擎測試 fixtures）。
   * 卡牌模式不用積分；下次清理可一併移除（含各測試 fixture）。
   */
  playerStats: RoundMatchStats;
  aiStats: RoundMatchStats;
  playerUsedEls: number[];
  aiUsedEls: number[];
  /**
   * Master RNG seed for this session. Set by initBattle (client-generated,
   * echoed to server so state is reproducible). Optional — legacy / test
   * fixtures leave it unset (treated as 0 by `cloneState`).
   */
  seed?: number;
  /**
   * Independent u32 seed used by `generateTerrain` to produce
   * `state.terrain`. Must agree across server and both PvP clients.
   *
   * Single-player: client picks at `Game.startSinglePlayer` (so terrain
   *   can render during DEPLOY, before initBattle), then ships it to the
   *   server inside `initBattle`. Server regenerates terrain locally
   *   from the same seed for authoritative effects.
   * PvP: server picks at `createRoom`, returns the same seed in both
   *   `createRoom` and `joinRoom` responses so each client computes the
   *   same map. `initBattle` then echoes it back; server prefers the
   *   value already stamped on the room battle.
   *
   * Optional for back-compat — legacy state with no terrain leaves this
   * undefined (treated as 0 + empty terrain by `cloneState`).
   */
  terrainSeed?: number;
  /**
   * Authoritative terrain grid. World coords (matches `units.pos` /
   * `bases.tiles`). Generated once at `initBattle` time; mutated only
   * by Fire / Fireball ignition (`grass` → `burningGrass`) during EXEC.
   * Optional for back-compat — legacy state has no terrain.
   */
  terrain?: TerrainGrid;
  /**
   * Card Tactician (M3) — when true the schedule validator switches from
   * the三消 Points & Tiers gate (`validateSchedule`) to the card gate
   * (`validateCardSchedule`): elements come from the player's hand, not a
   * points pool, so the powerLevel/points/duplicate-element checks no longer
   * apply. Set once at battle init via `StateManager.setCardMode(true)`
   * (single-player today; PvP DeckMirror in M6). Optional — undefined /
   * false keeps the legacy三消 validation path byte-for-byte.
   */
  cardMode?: boolean;
}

export type ServerPhase = 'match3' | 'sched' | 'exec' | 'over';

/** 三消遺留型別（僅 ServerState 的 dead 欄位仍引用，見上）。 */
export type RoundMatchStats = Record<number, number>;

// =============================================================
// COMMANDS (Client → Server)
// =============================================================

/** 出牌指令（一個 tick 一筆）。 */
export interface SpellCommand {
  slotIndex: number;
  /** ElementType index (0 = Pass). */
  spellType: number;
  /** Power multiplier (1..SPELL_LEVEL_MAX, derived from accumulated points via `pointsToLevel`). */
  powerLevel: number;
  /**
   * Card Tactician (路線圖 M2) — 疊加張數。同 tick + 同目標 + 同元素的
   * K 張疊加牌合併成一筆 cast，帶 stackCount=K。
   *
   * 提供時（疊加牌路徑）：spell.ts 改用「單張基礎值 × stackMultiplier(K)」
   * 計算效果，**忽略 powerLevel**。未提供時（效果牌 / 三消遺留）：照舊走
   * powerLevel 查表路徑（向後相容，重構期不會一次崩）。
   *
   * slotIndex 沿用為 tick index（0/1/2）。
   */
  stackCount?: number;
  /** Required for damage/heal/buff/debuff spells. */
  targetPos?: Pos;
}

/** One team's three-slot schedule for a round (may include Pass = type 0). */
export type SpellSchedule = [SpellCommand, SpellCommand, SpellCommand];

/**
 * Both teams' spell schedules for the upcoming Execution phase.
 *
 * NB: the field names `playerSpells` / `aiSpells` are a Phase 3a-3c
 * historical artifact from when team 1 was always AI. The server-side
 * code does NOT special-case which side is human — `playerSpells` is
 * just the team-0 schedule, `aiSpells` the team-1 schedule. PvP code
 * paths added in Phase 3d send REAL player schedules in `aiSpells`.
 *
 * Use {@link spellsForTeam} to read schedules in team-indexed loops
 * instead of branching on `team === 0 ? cmds.playerSpells : cmds.aiSpells`
 * every time.
 */
export interface TickCommands {
  /** Team 0 schedule (host / createRoom caller in PvP). */
  playerSpells: SpellSchedule;
  /** Team 1 schedule (joiner in PvP, or AI in single-player). */
  aiSpells: SpellSchedule;
}

/** Team-indexed accessor for {@link TickCommands}. */
export function spellsForTeam(
  cmds: TickCommands,
  team: 0 | 1,
): SpellSchedule {
  return team === 0 ? cmds.playerSpells : cmds.aiSpells;
}

/** Build {@link TickCommands} from team-indexed schedules. */
export function makeTickCommands(
  team0Spells: SpellSchedule,
  team1Spells: SpellSchedule,
): TickCommands {
  return { playerSpells: team0Spells, aiSpells: team1Spells };
}

// =============================================================
// VALIDATION RESULTS
// =============================================================

export interface ValidationResult {
  valid: boolean;
  reason?: string;
}

// =============================================================
// EVENTS (Server → Client)
// =============================================================
//
// Analogue of SRS §2.7 Event Log. The current single-machine
// implementation produces TimelineStep[] with apply() closures;
// the server-authoritative form uses these plain-data events
// so they can be serialised over the wire.

export interface TickLabelEvent {
  type: 'tickLabel';
  tick: number;
  dur: number;
}

export interface SpellImpact {
  unitId: string;
  /** Damage dealt (for damage/debuff spells). */
  dmg?: number;
  /** Healing applied (for heal spells, Session 6b: positive count of soldiers restored). */
  heal?: number;
  /** Buff power applied (for buff spells). */
  buff?: number;
  /**
   * Session 7 — physical-damage reduction fraction applied this tick by
   * Wind / Shield Wall (defense type). Range 0..0.6. Render hint only;
   * the actual reduction lives on `BattleUnit.tickDefPhys`.
   */
  defPhys?: number;
  /**
   * Session 7 — magical-damage reduction fraction applied this tick by
   * Wind / Shield Wall. Range 0..0.9. Mirrors `BattleUnit.tickDefMag`.
   */
  defMag?: number;
  /**
   * Session 11 (Leader CCC) — rooted ticks applied by the `growGrass`
   * effect when the target is standing on pre-existing grass. Value is
   * the modifier's initial `ticksRemaining` (= the SpellDef's
   * `rootDurationTicks`, default 2 for hostile phase). The client mirrors
   * this into a `'rooted'` UnitModifier so the badge renderer can show
   * a chain icon on the affected unit. Always positive; zero/undefined
   * means no root.
   */
  rooted?: number;
  /**
   * Session 11 follow-up — defense-down (vulnerability) amount applied by
   * the `growGrass` effect when the target is standing on a non-grass
   * cell. Value is the modifier's per-instance contribution (e.g. 0.05
   * = +5% damage taken). Positive ≤ 1. Sum-aggregated client-side into
   * `BattleUnit.dmgTakenMult`. Mutually exclusive with `rooted` on the
   * same impact (a single Grow cast picks one branch per target).
   */
  defDown?: number;
  /** True if this impact killed the unit. */
  died?: boolean;
  /** Post-impact values (so client can validate/replay). */
  totalSoldiers: number;
}

export interface SpellCastRecord {
  team: number;
  cmd: SpellCommand;
  sp: SpellDef;
  tgt: Pos;
  impacts: SpellImpact[];
  /**
   * Leader feature (Session 9) — whirlwind spells displace units
   * instead of dealing damage / healing. Each entry is one unit that
   * actually moved (blocked moves are silently dropped at resolution
   * time so this list is the authoritative "what visible motion to
   * play" record). Empty / omitted for non-whirlwind spells.
   *
   * `subPhase` mirrors MoveRecord's existing convention (0 here — we
   * don't split whirlwind into sub-phases) so the Renderer can reuse
   * the same animation path.
   */
  moves?: MoveRecord[];
}

/**
 * Friendly spells (heal + buff) resolve at tick START — the caster's allies
 * benefit before they move/fight. Hostile spells (damage + debuff) resolve
 * at tick END (after combat, before contest) — see SRS §3.3.
 */
export type SpellPhase = 'friendly' | 'hostile';

export interface SpellEvent {
  type: 'spell';
  tick: number;
  dur: number;
  /** Which sub-step of the tick this spell event belongs to (Session 6a). */
  phase: SpellPhase;
  spells: SpellCastRecord[];
}

export interface MoveRecord {
  unitId: string;
  from: Pos;
  to: Pos;
  /** 0 = first sub-phase (all units), 1 = second sub-phase (speed>=2). */
  subPhase: number;
}

export interface ContestedRecord {
  pos: Pos;
  unitAId: string;
  unitAFrom: Pos;
  unitBId: string;
  unitBFrom: Pos;
}

export interface MoveEvent {
  type: 'move';
  tick: number;
  dur: number;
  moves: MoveRecord[];
  contested: ContestedRecord[];
}

export interface AttackRecord {
  attackerId: string;
  attackerPos: Pos;
  targetUnitId: string | null;
  targetUnitPos: Pos | null;
  targetBaseTeam: number | null;
  targetBasePos: Pos | null;
  dmg: number;
  targetDied: boolean;
  baseDied: boolean;
}

export interface UnitDamageRecord {
  unitId: string;
  dmg: number;
  died: boolean;
  /** Post-damage values. */
  totalSoldiers: number;
}

export interface BaseDamageRecord {
  team: number;
  dmg: number;
  destroyed: boolean;
  hp: number;
}

/**
 * One attack-type group inside a combat event. Groups are emitted in
 * SRS §3.3.4 order: Mage → Archer → Cavalry → Infantry. Within a group,
 * all attacks resolve simultaneously; between groups, damage is visible.
 * The `unitDamages`/`baseDamages` lists are the animation-synced
 * analogue of BattleDirector's `groupApplies` — clients apply them
 * when the group's animation begins.
 */
export interface CombatGroup {
  groupType: string;
  attacks: AttackRecord[];
  unitDamages: UnitDamageRecord[];
  baseDamages: BaseDamageRecord[];
}

export interface CombatEvent {
  type: 'combat';
  tick: number;
  dur: number;
  groups: CombatGroup[];
}

export interface RetreatRecord {
  unitId: string;
  from: Pos;
  to: Pos;
}

export interface ContestResolveEvent {
  type: 'contestResolve';
  tick: number;
  dur: number;
  retreats: RetreatRecord[];
  stays: { unitId: string; pos: Pos }[];
}

export interface GameEndEvent {
  type: 'gameEnd';
  /** 0 = player wins, 1 = AI wins, -1 = draw/round-limit. */
  winner: number;
  dur: number;
}

/**
 * Emitted in Step D's wake when a Fire / Fireball spell ignites grass
 * cells inside its radius (a-2). Each cell listed has just transitioned
 * from `'grass'` to `'burningGrass'` on the server's authoritative
 * `state.terrain`. Coords are WORLD; `Game._flipEventsForView` flips
 * them to view coords for the team-1 client.
 */
export interface TerrainIgniteEvent {
  type: 'terrainIgnite';
  tick: number;
  dur: number;
  cells: Pos[];
}

/**
 * Emitted in Step D's wake when the Grow (Leader CCC) spell transitions
 * cells in its radius via `growTransition`. Each entry carries both the
 * world-coord position AND the destination terrain so the client can
 * apply the change idempotently — re-applying the spell's transition
 * table on the client side would NOT be safe because the transitions
 * are asymmetric (e.g. `'puddle' → 'swamp'` and `'swamp' → 'grass'`,
 * so if the client already predicted the change and turned a puddle
 * into a swamp, re-running the table would turn it into grass).
 *
 * Coords are WORLD; `Game._flipEventsForView` flips them to view coords
 * for the team-1 client. The `to` field is invariant under flip.
 *
 * Suppressed (not emitted) if `cells.length === 0`.
 */
export interface TerrainGrowEvent {
  type: 'terrainGrow';
  tick: number;
  dur: number;
  cells: { x: number; y: number; to: TerrainCell }[];
}

/**
 * Emitted in Step D's wake when the Rain (Leader CCC) spell transitions
 * cells in its radius via `rainTransition`. Same shape as
 * `TerrainGrowEvent`; sourced separately so client visuals can fan out.
 */
export interface TerrainRainEvent {
  type: 'terrainRain';
  tick: number;
  dur: number;
  cells: { x: number; y: number; to: TerrainCell }[];
}

/**
 * Session 12 (Leader CCC, Rockfall) — emitted in Step D's wake when
 * Rockfall lands on an empty cell and drops a `'boulder'` block. Coords
 * are WORLD; `Game._flipEventsForView` flips them to view coords for
 * the team-1 client. Suppressed (not emitted) if `cells.length === 0`.
 */
export interface TerrainBoulderFallEvent {
  type: 'terrainBoulderFall';
  tick: number;
  dur: number;
  cells: Pos[];
}

/**
 * Session 12 (Leader CCC, Rockfall counter) — emitted in Step D's wake
 * when a Fire / Lightning damage effect with `breakBoulder: true`
 * shatters one or more `'boulder'` cells in its spell radius back to
 * `'none'`. Same world-coord / flip semantics as
 * `TerrainBoulderFallEvent`. Suppressed if `cells.length === 0`.
 */
export interface TerrainBoulderBreakEvent {
  type: 'terrainBoulderBreak';
  tick: number;
  dur: number;
  cells: Pos[];
}

/**
 * Emitted at Step F (a-2) — the new "Terrain DoT" sub-step. One entry
 * per unit standing on `'burningGrass'` at tick end. Damage is computed
 * as `ceil(maxSoldiers × 0.15)` then reduced by `tickDefMag` (DoT counts
 * as magical for shielding purposes, per design decision Q8).
 *
 * Step F runs AFTER Step E contest, BEFORE the per-tick reset of
 * `buffAtk` / `tickDefPhys` / `tickDefMag`, so Shield Wall cast in
 * Step A of the same tick still protects against the DoT in Step F.
 *
 * The event is suppressed (not emitted at all) if `applies.length === 0`
 * — a tick with no burning-grass victims is silent on the wire.
 */
export interface TerrainDotEvent {
  type: 'terrainDot';
  tick: number;
  dur: number;
  applies: {
    unitId: string;
    /** Final damage after `tickDefMag` reduction (≥ 0). */
    dmg: number;
    /** Post-damage `totalSoldiers`. */
    totalSoldiers: number;
    died: boolean;
  }[];
}

export type BattleEvent =
  | TickLabelEvent
  | SpellEvent
  | MoveEvent
  | CombatEvent
  | ContestResolveEvent
  | TerrainIgniteEvent
  | TerrainGrowEvent
  | TerrainRainEvent
  | TerrainBoulderFallEvent
  | TerrainBoulderBreakEvent
  | TerrainDotEvent
  | GameEndEvent;
